import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus } from 'lucide-react';
import { Button } from './ui/button';

/**
 * HumaTwin Page Header Component
 * Consistent header across all pages with title, subtitle, and optional actions
 */
export function PageHeader({ 
  title, 
  subtitle, 
  backPath, 
  backLabel,
  action,
  actionLabel,
  actionIcon: ActionIcon = Plus,
  onAction,
  children 
}) {
  const navigate = useNavigate();

  return (
    <div className="ht-page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div className="flex items-start gap-4">
        {backPath && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(backPath)}
            className="mt-1 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 ltr:mr-1 rtl:ml-1" />
            <span className="hidden sm:inline">{backLabel}</span>
          </Button>
        )}
        <div>
          <h1 className="ht-page-title">{title}</h1>
          {subtitle && <p className="ht-page-subtitle text-sm">{subtitle}</p>}
        </div>
      </div>
      
      {(action || onAction) && (
        <Button onClick={onAction} className="gap-2">
          <ActionIcon className="w-4 h-4" />
          {actionLabel}
        </Button>
      )}
      
      {children}
    </div>
  );
}

/**
 * HumaTwin Status Badge Component
 */
export function StatusBadge({ variant = 'default', children, className = '' }) {
  const variants = {
    default: 'ht-badge-gray',
    blue: 'ht-badge-blue',
    green: 'ht-badge-green',
    amber: 'ht-badge-amber',
    purple: 'ht-badge-purple',
    success: 'ht-badge-green',
    warning: 'ht-badge-amber',
    info: 'ht-badge-blue',
  };

  return (
    <span className={`ht-badge ${variants[variant] || variants.default} ${className}`}>
      {children}
    </span>
  );
}

/**
 * HumaTwin Stat Card Component
 */
export function StatCard({ value, label, className = '' }) {
  return (
    <div className={`ht-stat-card ${className}`}>
      <div className="ht-stat-value">{value}</div>
      <div className="ht-stat-label">{label}</div>
    </div>
  );
}

/**
 * HumaTwin Card Component
 */
export function HTCard({ children, className = '', noPadding = false }) {
  return (
    <div className={`ht-card ${noPadding ? '' : 'p-6'} ${className}`}>
      {children}
    </div>
  );
}

/**
 * HumaTwin Tab Component
 */
export function TabGroup({ tabs, activeTab, onTabChange, className = '' }) {
  return (
    <div className={`ht-tabs ${className}`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={`ht-tab ${activeTab === tab.id ? 'active' : ''}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/**
 * HumaTwin Action Chip Component
 */
export function ActionChip({ icon: Icon, children, onClick, className = '' }) {
  return (
    <button 
      onClick={onClick} 
      className={`ht-action-chip ${className}`}
    >
      {Icon && <Icon className="w-4 h-4 text-gray-500" />}
      {children}
    </button>
  );
}

/**
 * HumaTwin Empty State Component
 */
export function HTEmptyState({ 
  icon: Icon, 
  title, 
  description, 
  actionLabel, 
  onAction,
  className = '' 
}) {
  return (
    <div className={`flex flex-col items-center justify-center py-12 px-4 text-center ${className}`}>
      {Icon && (
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <Icon className="w-8 h-8 text-gray-400" />
        </div>
      )}
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      {description && <p className="text-gray-500 max-w-sm mb-6">{description}</p>}
      {actionLabel && onAction && (
        <Button onClick={onAction}>
          <Plus className="w-4 h-4 mr-2" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

export default PageHeader;
